<template>
  <div class="sd">
    <div class="sd-chart w-chart">
      <div class="w-toolbar">
        <div class="w-chips">
          <span class="chip"><b>{{ fmt(totalSplits) }}</b> splits</span>
          <span class="chip"><b>{{ fmt(totalTransfers) }}</b> transfers</span>
          <span class="chip accent" v-if="best"><i class="fas fa-bolt"></i> best month {{ best.label }}: <b>{{ fmt(best.splits) }}</b></span>
        </div>
      </div>
      <div class="w-plot">
        <apexchart :key="chartKey" v-if="months.length" type="bar" height="100%" :options="options" :series="series"/>
        <div v-else class="w-empty"><i class="fas fa-hand-holding-heart"></i>{{ stats.insights ? 'No splits delivered yet' : 'Loading…' }}</div>
      </div>
    </div>
    <div class="sd-companies" v-if="companies.length">
      <div class="sd-title">By company</div>
      <div v-for="(c, i) in companies" :key="c.company" class="sd-row w-row-in" :style="{ animationDelay: (i * 0.07) + 's' }">
        <span class="sd-avatar" :style="{ background: c.color }">{{ initials(c.company) }}</span>
        <div class="sd-main">
          <div class="sd-line"><span class="sd-name">{{ c.company }}</span><b>{{ compact(c.splits) }}</b></div>
          <div class="w-bar"><span :style="{ '--w': c.share + '%', background: c.color, animationDelay: (0.2 + i * 0.07) + 's' }"></span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { apexBase, compact, fmt, monthLabel, palette, seriesColors, themeState } from '../theme';

export default {
  name: 'SplitsDelivered',
  components: { apexchart: VueApexCharts },
  props: { stats: { type: Object, required: true } },
  computed: {
    // a theme or data change re-creates the chart: ApexCharts does not always redraw mixed charts on update
    chartKey() { return `${themeState.tick}:${JSON.stringify(this.series)}`; },
    months() {
      const t = (this.stats.insights && this.stats.insights.timeline) || [];
      const first = t.findIndex((m) => m.splits > 0);
      return first < 0 ? [] : t.slice(first).map((m) => ({ ...m, label: monthLabel(m.month) }));
    },
    totalSplits() { return this.months.reduce((a, m) => a + m.splits, 0); },
    totalTransfers() { return this.months.reduce((a, m) => a + m.transfers, 0); },
    best() { return this.months.length ? [...this.months].sort((a, b) => b.splits - a.splits)[0] : null; },
    companies() {
      const list = (this.stats.insights && this.stats.insights.companies) || [];
      const max = Math.max(1, ...list.map((c) => c.splits));
      const colors = seriesColors();
      return list.slice(0, 6).map((c, i) => ({ ...c, share: Math.max(3, (c.splits / max) * 100), color: colors[i % colors.length] }));
    },
    series() { return [{ name: 'Splits delivered', data: this.months.map((m) => m.splits) }]; },
    options() {
      void themeState.tick;
      const p = palette();
      return apexBase({
        colors: [p.green],
        xaxis: { categories: this.months.map((m) => m.label) },
        yaxis: { labels: { formatter: (v) => compact(v) } },
        plotOptions: { bar: { columnWidth: '58%', borderRadius: 6, borderRadiusApplication: 'end' } },
        fill: { type: 'gradient', gradient: { type: 'vertical', shadeIntensity: 0.4, gradientToColors: [p.teal], opacityFrom: 1, opacityTo: 0.85, stops: [0, 100] } },
        tooltip: { y: { formatter: (v, { dataPointIndex }) => `${fmt(v)} splits · ${fmt(this.months[dataPointIndex].transfers)} transfers` } },
      });
    }
  },
  methods: {
    fmt,
    compact,
    initials(name) { return String(name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase(); }
  }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.sd { flex: 1 1 auto; min-height: 0; display: flex; }
.sd-chart { flex: 1 1 auto; min-width: 0; }

.sd-companies {
  flex: 0 0 34%;
  max-width: 260px;
  padding: 6px 16px 12px 4px;
  overflow: auto;
}

.sd-title {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.sd-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; }

.sd-avatar {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 10px;
  color: #fff;
  font-weight: 800;
  font-size: 0.72rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sd-main { flex: 1 1 auto; min-width: 0; }
.sd-line { display: flex; justify-content: space-between; gap: 6px; font-size: 0.78rem; margin-bottom: 4px; b { color: var(--text-heading); } }
.sd-name { color: var(--text-main); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

@media (max-width: 700px) {
  .sd { flex-direction: column; }
  .sd-companies { max-width: none; flex: 0 0 auto; padding: 0 16px 12px; }
}
</style>
